import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, Pencil, AlertTriangle, Mail, Plus, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DOC_TYPES = ["contract", "sow", "amendment", "sla", "nda", "other"];

export default function Documents() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [docType, setDocType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [expiring, setExpiring] = useState([]);

  const isAdmin = user?.role === "crm_manager";

  const load = async () => {
    setLoading(true);
    try {
      const [d, c, ex] = await Promise.all([
        api.get("/documents", { params: {
          client_id: clientId || undefined,
          document_type: docType || undefined,
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
        } }),
        api.get("/clients", { params: { page_size: 200 } }),
        api.get("/documents/expiring", { params: { days: 30 } }).catch(() => ({ data: [] })),
      ]);
      setRows(d.data); setClients(c.data.items); setExpiring(ex.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, docType, fromDate, toDate]);

  const download = async (doc) => {
    try {
      const t = localStorage.getItem("mptg_token");
      const res = await fetch(`${API}/documents/${doc.id}/download`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = doc.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const del = async (doc) => {
    if (!window.confirm(`Delete ${doc.file_name}?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="documents-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mptg-overline">Repository</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Documents</h1>
          <p className="text-slate-500 mt-1">{rows.length} document{rows.length === 1 ? "" : "s"} stored</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="upload-doc-btn">
              <Upload className="w-4 h-4 mr-1" /> Upload document
            </Button>
          </DialogTrigger>
          <UploadDialog clients={clients} onDone={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      {expiring.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 p-4" data-testid="expiring-banner">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="w-4 h-4" /> {expiring.length} document{expiring.length === 1 ? "" : "s"} expiring in the next 30 days
          </div>
          <ul className="mt-2 text-sm space-y-1">
            {expiring.slice(0, 5).map((d) => (
              <li key={d.id} className="flex items-center justify-between">
                <span><strong>{d.file_name}</strong> · {d.client_name}</span>
                <span className="text-xs">
                  {d.days_until_expiry} day{d.days_until_expiry === 1 ? "" : "s"} left · {d.validity_till}
                </span>
              </li>
            ))}
            {expiring.length > 5 && <li className="text-xs text-amber-700">+ {expiring.length - 5} more</li>}
          </ul>
          <div className="text-xs mt-2 flex items-center gap-1">
            <Mail className="w-3.5 h-3.5" />
            Daily reminder emails are queued to each stakeholder (MOCKED — visible in backend email_log).
          </div>
        </div>
      )}

      <div className="mptg-card flex flex-wrap gap-3 items-center">
        <Select value={clientId || "all"} onValueChange={(v) => setClientId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64" data-testid="doc-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={docType || "all"} onValueChange={(v) => setDocType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="doc-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>From</span>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-36" data-testid="doc-from-date" />
          <span>To</span>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-36" data-testid="doc-to-date" />
        </div>
        {(clientId || docType || fromDate || toDate) && (
          <button
            onClick={() => { setClientId(""); setDocType(""); setFromDate(""); setToDate(""); }}
            className="text-xs text-[#2E86C1] hover:underline"
            data-testid="doc-clear-filters"
          >
            Clear
          </button>
        )}
      </div>

      <div className="mptg-card p-0 overflow-hidden">
        <table className="w-full mptg-table">
          <thead><tr><th>File</th><th>Client</th><th>Type</th><th>Validity</th><th>Stakeholders</th><th>Uploaded</th><th className="text-right">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="py-8 text-center text-slate-400">Loading…</td></tr> :
             rows.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-slate-400">No documents yet.</td></tr> :
              rows.map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                  <td className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <div>
                      <div className="font-medium">{d.file_name}</div>
                      <div className="text-xs text-slate-500">{formatSize(d.size)}</div>
                    </div>
                  </td>
                  <td>{d.client_name}</td>
                  <td><span className="badge-blue rounded px-2 py-0.5 text-xs font-semibold uppercase">{d.document_type}</span></td>
                  <td className="text-xs">
                    {d.validity_from || d.validity_till ? (
                      <div className="whitespace-nowrap">
                        <div className="text-slate-600">From: <span className="font-mono">{d.validity_from || "-"}</span></div>
                        <div className={(d.validity_till && new Date(d.validity_till) < new Date()) ? "text-[#C0392B] font-semibold" : "text-slate-600"}>
                          Till: <span className="font-mono">{d.validity_till || "-"}</span>
                        </div>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="text-xs">
                    {(d.stakeholders || []).length > 0 ? (
                      <span className="badge-green rounded px-1.5 py-0.5 font-semibold">{d.stakeholders.length}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="text-xs text-slate-500">{(d.uploaded_at || "").slice(0, 10)}</td>
                  <td className="text-right whitespace-nowrap space-x-2">
                    <button onClick={() => download(d)} className="text-[#2E86C1] hover:underline text-xs inline-flex items-center gap-1" data-testid={`download-${d.id}`}>
                      <Download className="w-3.5 h-3.5" /> Download
                    </button>
                    {isAdmin && (
                      <>
                        <button onClick={() => setEditDoc(d)} className="text-[#2E86C1] hover:underline text-xs inline-flex items-center gap-1" data-testid={`edit-doc-${d.id}`}>
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button onClick={() => del(d)} className="text-[#C0392B] hover:underline text-xs inline-flex items-center gap-1" data-testid={`delete-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {editDoc && (
        <Dialog open={true} onOpenChange={(v) => !v && setEditDoc(null)}>
          <EditDocDialog doc={editDoc} onDone={() => { setEditDoc(null); load(); }} />
        </Dialog>
      )}
    </div>
  );
}

function formatSize(n) {
  if (!n) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function UploadDialog({ clients, onDone }) {
  const fileRef = useRef(null);
  const [clientId, setClientId] = useState("");
  const [docType, setDocType] = useState("contract");
  const [notes, setNotes] = useState("");
  const [validityFrom, setValidityFrom] = useState("");
  const [validityTill, setValidityTill] = useState("");
  const [stakeholders, setStakeholders] = useState([]);
  const [busy, setBusy] = useState(false);

  const addStakeholder = () => setStakeholders([...stakeholders, { name: "", email: "", phone: "" }]);
  const updateStakeholder = (i, k, v) => {
    const arr = [...stakeholders]; arr[i] = { ...arr[i], [k]: v }; setStakeholders(arr);
  };
  const removeStakeholder = (i) => setStakeholders(stakeholders.filter((_, idx) => idx !== i));

  const needsContractFields = docType === "contract" || docType === "amendment" || docType === "sla" || docType === "sow";

  const save = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Pick a file"); return; }
    if (!clientId) { toast.error("Pick a client"); return; }
    if (needsContractFields && validityFrom && validityTill && validityTill < validityFrom) {
      toast.error("Validity-Till must be after Validity-From"); return;
    }
    const cleanStakeholders = stakeholders
      .map((s) => ({ name: (s.name || "").trim(), email: (s.email || "").trim(), phone: (s.phone || "").trim() }))
      .filter((s) => s.name || s.email);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("client_id", clientId);
    fd.append("document_type", docType);
    fd.append("notes", notes);
    if (validityFrom) fd.append("validity_from", validityFrom);
    if (validityTill) fd.append("validity_till", validityTill);
    if (cleanStakeholders.length) fd.append("stakeholders", JSON.stringify(cleanStakeholders));
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-xl" data-testid="upload-doc-dialog">
      <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2 max-h-[70vh] overflow-auto">
        <div>
          <Label>File *</Label>
          <Input type="file" ref={fileRef} className="mt-1.5" data-testid="doc-file-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="doc-client-select"><SelectValue placeholder="Pick client" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Document type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {needsContractFields && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Validity from</Label>
                <Input type="date" value={validityFrom} onChange={(e) => setValidityFrom(e.target.value)} data-testid="doc-validity-from" />
              </div>
              <div>
                <Label className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Validity till</Label>
                <Input type="date" value={validityTill} onChange={(e) => setValidityTill(e.target.value)} data-testid="doc-validity-till" />
              </div>
            </div>
            <div className="rounded border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <Label className="mb-0">Stakeholders (for expiry reminders)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addStakeholder} data-testid="add-stakeholder-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2 mt-2">
                {stakeholders.length === 0 && (
                  <div className="text-xs text-slate-400">Add at least one stakeholder to receive expiry emails.</div>
                )}
                {stakeholders.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <Input className="col-span-3" placeholder="Name" value={s.name} onChange={(e) => updateStakeholder(i, "name", e.target.value)} data-testid={`stk-name-${i}`} />
                    <Input className="col-span-5" placeholder="Email" type="email" value={s.email} onChange={(e) => updateStakeholder(i, "email", e.target.value)} data-testid={`stk-email-${i}`} />
                    <Input className="col-span-3" placeholder="Phone" value={s.phone} onChange={(e) => updateStakeholder(i, "phone", e.target.value)} />
                    <button onClick={() => removeStakeholder(i)} className="col-span-1 text-[#C0392B] hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Starting 30 days before <strong>Validity till</strong>, reminders are queued daily.
              </div>
            </div>
          </>
        )}

        <div>
          <Label>Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={busy} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-doc-btn">
          {busy ? "Uploading…" : "Upload"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EditDocDialog({ doc, onDone }) {
  const [docType, setDocType] = useState(doc.document_type || "contract");
  const [notes, setNotes] = useState(doc.notes || "");
  const [validityFrom, setValidityFrom] = useState(doc.validity_from || "");
  const [validityTill, setValidityTill] = useState(doc.validity_till || "");
  const [stakeholders, setStakeholders] = useState(doc.stakeholders || []);
  const [saving, setSaving] = useState(false);

  const addStakeholder = () => setStakeholders([...stakeholders, { name: "", email: "", phone: "" }]);
  const updateStakeholder = (i, k, v) => {
    const arr = [...stakeholders]; arr[i] = { ...arr[i], [k]: v }; setStakeholders(arr);
  };
  const removeStakeholder = (i) => setStakeholders(stakeholders.filter((_, idx) => idx !== i));

  const save = async () => {
    if (validityFrom && validityTill && validityTill < validityFrom) {
      toast.error("Validity-Till must be after Validity-From"); return;
    }
    setSaving(true);
    try {
      const clean = stakeholders
        .map((s) => ({ name: (s.name || "").trim(), email: (s.email || "").trim(), phone: (s.phone || "").trim() }))
        .filter((s) => s.name || s.email);
      await api.patch(`/documents/${doc.id}`, {
        document_type: docType,
        notes,
        validity_from: validityFrom || null,
        validity_till: validityTill || null,
        stakeholders: clean,
      });
      toast.success("Document updated");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-xl" data-testid="edit-doc-dialog">
      <DialogHeader><DialogTitle>Edit document — {doc.file_name}</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2 max-h-[70vh] overflow-auto">
        <div>
          <Label>Document type</Label>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="edit-doc-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Validity from</Label>
            <Input type="date" value={validityFrom} onChange={(e) => setValidityFrom(e.target.value)} data-testid="edit-validity-from" />
          </div>
          <div>
            <Label>Validity till</Label>
            <Input type="date" value={validityTill} onChange={(e) => setValidityTill(e.target.value)} data-testid="edit-validity-till" />
          </div>
        </div>
        <div className="rounded border border-slate-200 p-3">
          <div className="flex items-center justify-between">
            <Label className="mb-0">Stakeholders ({stakeholders.length})</Label>
            <Button type="button" variant="outline" size="sm" onClick={addStakeholder}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-2 mt-2">
            {stakeholders.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <Input className="col-span-3" placeholder="Name" value={s.name || ""} onChange={(e) => updateStakeholder(i, "name", e.target.value)} />
                <Input className="col-span-5" placeholder="Email" type="email" value={s.email || ""} onChange={(e) => updateStakeholder(i, "email", e.target.value)} />
                <Input className="col-span-3" placeholder="Phone" value={s.phone || ""} onChange={(e) => updateStakeholder(i, "phone", e.target.value)} />
                <button onClick={() => removeStakeholder(i)} className="col-span-1 text-[#C0392B] hover:bg-red-50 rounded">
                  <Trash2 className="w-4 h-4 mx-auto" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-edit-doc">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
